import { Component } from "react";

// Off YouTube, the gate's heart is Slowed Studio's prompt box, framed from
// studio.slowed.app/embed/prompt/ (slowed-studio src/embed.js), the same one
// www.slowed.app's landing frames. A change to the messages is a change to
// both repos (slowed-studio src/ui/promptLink.js):
//
// - the frame says how tall it is: { type: HEIGHT_MESSAGE, height };
// - asked with `add=1`, the box has Studio's "Add a song", which comes back
//   the same way as Slow It, with Studio's file chooser address;
// - asked with `open=parent`, its Slow It doesn't navigate (it would take
//   the popup itself away) but hands Studio's address with the words in it
//   to the popup: { type: OPEN_MESSAGE, url }, which opens it in a tab.
//
// Studio lets this extension frame it (frame-ancestors, slowed-studio
// infrastructure/lib/tool-stack.ts, by extension id). If the frame hasn't
// said anything within LOAD_TIMEOUT_MS (offline, or a Studio that doesn't
// let it frame yet), `onUnavailable` lets the gate show its own buttons.

export const HEIGHT_MESSAGE = "slowed-studio:embed-height";
export const OPEN_MESSAGE = "slowed-studio:embed-open";
export const LOAD_TIMEOUT_MS = 5000;
const MIN_HEIGHT = 120;
const MAX_HEIGHT = 360;

function originOf(address) {
  try {
    return new URL(address).origin;
  } catch (_) {
    return null;
  }
}

// The frame's address, from Studio's (which may carry utm tags of its own).
export function promptEmbedSrc(toolUrl, { dark = false } = {}) {
  const origin = originOf(toolUrl);
  if (!origin) return null;
  const url = new URL("/embed/prompt/", origin);
  url.searchParams.set("from", "extension");
  url.searchParams.set("open", "parent");
  url.searchParams.set("focus", "1");
  // Studio's "Add a song" in the box, as on Studio's own prompt: it comes
  // back as an open message, for Studio's file chooser.
  url.searchParams.set("add", "1");
  url.searchParams.set("theme", dark ? "dark" : "light");
  return url.toString();
}

// What a message from the frame asks for, or null for anything else: only
// the frame itself, from Studio's origin, and only an address on Studio.
export function readEmbedMessage(event, { frameWindow, toolUrl }) {
  const origin = originOf(toolUrl);
  if (!origin || !event || event.origin !== origin) return null;
  if (frameWindow && event.source !== frameWindow) return null;
  const data = event.data || {};
  if (data.type === HEIGHT_MESSAGE && Number.isFinite(data.height)) {
    return {
      type: "height",
      height: Math.min(Math.max(Math.ceil(data.height), MIN_HEIGHT), MAX_HEIGHT),
    };
  }
  if (data.type === OPEN_MESSAGE && typeof data.url === "string") {
    if (originOf(data.url) !== origin) return null;
    return { type: "open", url: data.url };
  }
  return null;
}

export default class StudioPromptEmbed extends Component {
  constructor(props) {
    super(props);
    this.state = { height: null };
    this.frame = null;
    this.onMessage = this.onMessage.bind(this);
  }

  componentDidMount() {
    window.addEventListener("message", this.onMessage);
    this.timer = setTimeout(() => {
      if (this.state.height == null) this.props.onUnavailable?.();
    }, LOAD_TIMEOUT_MS);
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.onMessage);
    clearTimeout(this.timer);
  }

  onMessage(event) {
    const message = readEmbedMessage(event, {
      frameWindow: this.frame?.contentWindow,
      toolUrl: this.props.toolUrl,
    });
    if (!message) return;
    if (message.type === "height") {
      clearTimeout(this.timer);
      if (message.height !== this.state.height) this.setState({ height: message.height });
      return;
    }
    if (message.type === "open") this.props.onSlowIt?.(message.url);
  }

  render() {
    const { toolUrl, dark } = this.props;
    const src = promptEmbedSrc(toolUrl, { dark });
    if (!src) return null;
    const { height } = this.state;
    return (
      <iframe
        ref={(frame) => {
          this.frame = frame;
        }}
        className={`unsupported-gate__prompt${height ? " unsupported-gate__prompt--ready" : ""}`}
        title="Slowed Studio: slow down any song"
        src={src}
        // A frame whose color-scheme differs from the popup's is painted
        // opaque, so it takes the popup's.
        style={{
          height: `${height || 200}px`,
          colorScheme: dark ? "dark" : "light",
        }}
        // Focus the box as soon as it is there, so the listener can paste.
        onLoad={() => this.frame?.focus()}
      />
    );
  }
}
